<template>
  <div class="actions-view">
    <Button
      v-if="!isDetail"
      class="top-create--btn action-add-btn"
      icon="md-add"
      type="primary"
      ghost
      @click="showModal"
    >
      {{ $t('common.add') }}
    </Button>
    <Table :columns="actionsCol" :data="actions">
      <template slot-scope="{ row, index }" slot="action">
        <Button
          shape="circle"
          type="primary"
          size="small"
          ghost
          @click="viewAction(row, index)"
          style="margin-right: 8px"
        >
          {{ isDetail ? $t('common.view') : $t('common.edit') }}
        </Button>
        <Button v-if="!isDetail" shape="circle" type="error" size="small" ghost @click="removeAction(index)">
          {{ $t('common.delete') }}
        </Button>
      </template>
    </Table>

    <Modal
      v-model="viewModalVisible"
      class-name="action-modal--view view-form"
      :title="!isDetail ? actionTitle : $t('rules.viewAction')"
      :width="650"
      :mask-closable="false"
      @on-visible-change="handleChangeActionModel"
    >
      <Spin v-if="viewLoading" fix></Spin>
      <!-- add | edit: Add to show only reourseid   -->
      <Form ref="actionForm" :model="actionForm" :rules="actionRules">
        <Row>
          <i-col span="24">
            <!-- sink type -->
            <FormItem label="Sink" prop="sink">
              <template v-if="actionForm.sink && sinkInfo">
                <a class="sink-docs right-item" :href="i18nLabel(sinkInfo.helpUrl)" target="_blank" rel="noopener">
                  <Icon type="md-document" />
                  {{ $t('common.documentation') }}
                </a>
                <Tooltip v-if="sinkInfo.trial" :content="$t('plugins.betaTip')" placement="top">
                  <Alert class="beta-alert" type="error">Beta</Alert>
                </Tooltip>
              </template>
              <ConfKeyTypesSeletor
                v-model="actionForm.sink"
                :category="category"
                :disabled="isDetail"
                @onChange="changeSink"
              />
            </FormItem>

            <!-- resourceId -->
            <FormItem v-if="viewModalVisible" label="Resource ID" prop="resourceId">
              <Button
                v-if="actionForm.sink"
                icon="ios-add"
                type="primary"
                ghost
                class="top-create--btn action-add-btn"
                @click="showConfKeyModel()"
              >
                {{ $t('configuration.addSinkTemplate') }}
              </Button>
              <ConfKeysSelector
                ref="confKeysSelectorRef"
                v-model="actionForm.resourceId"
                :category="category"
                :type="actionForm.sink"
                @onChange="changeResourceID"
              />
            </FormItem>
          </i-col>
        </Row>
      </Form>

      <!-- Edit-->
      <!-- <section v-if="isEditAction"> -->
      <!-- propertyData -->
      <PropertyForm
        ref="propertyFormRef"
        :isDetail="isDetail"
        :properties="properties"
        :form="actionPropertiesForm"
        :type="actionForm.sink"
        :rule="true"
        :sameLevelFormValid="sameLevelFormValid"
      ></PropertyForm>

      <!-- advancedData -->
      <SinkBaseForm
        ref="advancedFormRef"
        :isDetail="isDetail"
        :form="actionAdvancedForm"
        :type="actionForm.sink"
        @changeConnectionSelector="changeConnectionSelector"
      />
      <!-- </section> -->

      <div slot="footer">
        <Button type="text" @click="viewModalVisible = false">{{ $t('common.cancel') }}</Button>
        <Button v-if="!isDetail" type="primary" @click="save">{{ $t('common.submit') }}</Button>
        <Button
          type="primary"
          ghost
          :loading="isLoadingTestConnection"
          class="btn-test-connection"
          @click="save('testConnection')"
        >
          {{ $t('common.testConnection') }}
        </Button>
      </div>
    </Modal>

    <!-- conf key config -->
    <ConfKeyPeopertiesModal
      ref="confKeyPropertiesRef"
      :modalVisible.sync="confKeyPropertiesVisible"
      :title="modalTitle"
      :category="category"
      :type="actionForm.sink"
      :confKeyName="actionForm.resourceId"
      :isEdit="isEditResourceID"
      :isCanAdvanced="true"
      @submit="submitedResourceID"
    />
  </div>
</template>

<script>
import { cloneDeep } from 'lodash'
import { getMetadataByCategory, getMetadataYamlByCategory, testConnectionByCategory } from '@/api/metadata'
import SinkBaseForm from './SinkBaseForm.vue'
import PropertyForm from '../../../components/PropertyForm.vue'
import currentLang from '@/mixins/currentLang'
import ConfKeyPeopertiesModal from '@/views/configuration/resource/components/ConfKeyPropertiesModal.vue'
import ConfKeysSelector from '@/components/ConfKeysSelector.vue'
import ConfKeyTypesSeletor from '@/components/ConfKeyTypesSeletor.vue'
import { removeObjectEmptyValue } from '@/utils/handleData'
import { RESOURCE_TYPE } from '@/utils/constants'

export default {
  name: 'Actions',

  mixins: [currentLang],

  components: {
    PropertyForm,
    SinkBaseForm,
    ConfKeyPeopertiesModal,
    ConfKeysSelector,
    ConfKeyTypesSeletor,
  },

  props: {
    actions: {
      type: Array,
      required: true,
    },
    nodeID: {
      type: String,
      required: true,
    },
    isDetail: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      category: RESOURCE_TYPE.sinks,
      editIndex: null,
      isEditAction: false,
      viewModalVisible: false,
      viewLoading: false,
      actionForm: {
        sink: '',
        resourceId: '',
      },
      propertiesFormTransition: {},
      properties: [],
      actionPropertiesForm: {},
      actionAdvancedForm: {},
      actionTitle: '',
      allResourceData: {},
      sinkInfo: {
        helpUrl: {},
      },
      actionsCol: [
        {
          title: 'Sink',
          key: 'sink',
        },
        {
          title: this.$t('common.operations'),
          slot: 'action',
          width: 250,
        },
      ],
      actionRules: {
        sink: [{ required: true, message: this.$t('rules.sinkRequired') }],
        resourceId: [{ required: false, message: this.$t('rules.resourceIdRequired') }],
      },
      isLoadingTestConnection: false,
      isEditResourceID: false,
      confKeyPropertiesVisible: false,
      sameLevelFormValid: false,
    }
  },

  computed: {
    modalTitle() {
      return !this.isEditResourceID
        ? this.$t('configuration.addSinkTemplate')
        : this.$t('configuration.editSinkTemplate')
    },

    connectionSeletorValue() {
      return (value, sink) => {
        if (!value) return value
        const newValue = value.includes(`${sink}.`) ? value.split('.')[1] : value
        return newValue
      }
    },
  },

  methods: {
    // show action modal: add | edit action
    showModal() {
      this.viewModalVisible = true
      this.actionTitle = this.$t('rules.addAction')
      this.actionPropertiesForm = {}
    },
    handleChangeActionModel(val) {
      if (!val) {
        this.$refs.actionForm.resetFields()
        this.$refs.propertyFormRef.resetFields()
        this.$refs.advancedFormRef.resetFields()
        this.isEditAction = false
        this.actionTitle = ''
        this.editIndex = null
        this.properties = []
        this.propertiesFormTransition = {}
        this.actionForm = {
          sink: '',
          resourceId: '',
        }
      }
    },

    // edit action
    viewAction(row, index) {
      this.viewModalVisible = true
      this.isEditAction = true
      this.editIndex = index
      this.actionTitle = this.$t('rules.editAction')
      const actionData = row.data[row.sink]

      // get action detail
      const { resourceId } = actionData

      this.actionForm = {
        sink: row.sink,
        resourceId,
      }

      // get properties,
      this.loadSinkProperties(this.actionForm)

      const actionDataKeys = Object.keys(actionData)
      // properties form *& advanced form
      if (actionDataKeys.length && actionDataKeys.length === 1 && actionDataKeys[0] === 'resourceId') {
        // first edit after add action
        this.changeResourceID(resourceId)
      } else {
        this.getFormData(actionData, row.sink)
      }
    },

    getFormData(data, sink) {
      const currentSink = sink || this.actionForm.sink

      const {
        resourceId,
        connectionSelector,
        omitIfEmpty,
        sendSingle,
        format,
        schemaName,
        schemaMessage,
        delimiter: delimited,
        dataTemplate,
        concurrency,
        bufferLength,
        enableCache,
        memoryCacheThreshold,
        maxDiskCache,
        cleanCacheAtStop,
        bufferPageSize,
        resendInterval,
        runAsync,
        ...propertiesData
      } = data

      // advanced form
      const curtConnectionSelector = connectionSelector
        ? this.connectionSeletorValue(connectionSelector, currentSink)
        : ''
      this.actionAdvancedForm = {
        connectionSelector: curtConnectionSelector,
        omitIfEmpty,
        sendSingle,
        format,
        schemaName,
        schemaMessage,
        delimited,
        dataTemplate,
        concurrency,
        bufferLength,
        enableCache,
        memoryCacheThreshold,
        maxDiskCache,
        cleanCacheAtStop,
        bufferPageSize,
        resendInterval,
        runAsync,
      }

      this.propertiesFormTransition = cloneDeep(propertiesData)

      // properties form
      if (curtConnectionSelector) {
        // cover properties form with connectionseletor data
        this.getConnectionSeletorData(this.actionForm.sink).then((propertiesFormData) => {
          this.actionPropertiesForm = {
            ...this.propertiesFormTransition,
            ...propertiesFormData,
          }
        })
      } else {
        this.actionPropertiesForm = {
          ...this.propertiesFormTransition,
        }
      }
    },

    // Get peroperties && computed action data
    loadSinkProperties({ sink }) {
      const data = { nodeID: this.nodeID, name: sink }
      return new Promise((resolve, reject) => {
        getMetadataByCategory(this.category, data)
          .then((res) => {
            const { data: propertiesData } = res
            let { properties } = propertiesData || {}
            properties = (properties || []).filter((item) => item.name !== 'connectionSelector')
            this.properties = properties || []
            this.sinkInfo = propertiesData.about
            resolve(this.properties)
          })
          .catch((error) => {
            reject(error)
          })
      })
    },

    // Get connectionSelector data
    getConnectionSeletorData(type) {
      return new Promise((resolve, reject) => {
        const data = { nodeID: this.nodeID, name: type }
        getMetadataYamlByCategory('connections', data)
          .then((res) => {
            const { data: allConnectionSelectorData } = res

            const curtConnectionSelector = this.actionAdvancedForm.connectionSelector
            const connectionSelectorData = allConnectionSelectorData[curtConnectionSelector]
            connectionSelectorData.clientId = connectionSelectorData.clientid

            const propertieServer = this.properties.find((it) => it.name === 'server')
            if (propertieServer && propertieServer.type === 'string') {
              connectionSelectorData.server = connectionSelectorData.servers ? connectionSelectorData.servers[0] : ''
            }

            // cover properties some value
            this.properties.forEach((item) => {
              const value = connectionSelectorData[item.name]
              if (value) {
                this.propertiesFormTransition[item.name] = value
              }
            })
            resolve(this.propertiesFormTransition)
          })
          .catch((error) => {
            reject(error)
          })
      })
    },

    // chang sink type
    async changeSink() {
      this.actionForm.resourceId = ''
      this.properties = []
      this.actionPropertiesForm = {}
      this.actionAdvancedForm = {}
      this.loadSinkProperties(this.actionForm)
    },

    // Change conf key
    changeResourceID(resourceId) {
      this.actionPropertiesForm = {}
      if (this.actionForm.sink && resourceId) {
        const data = { nodeID: this.nodeID, name: this.actionForm.sink }
        getMetadataYamlByCategory(this.category, data).then((res) => {
          const { data: allConfKeysData } = res

          if (Object.keys(allConfKeysData).length) {
            const currentSinkReourceData = allConfKeysData[resourceId]
            this.getFormData(currentSinkReourceData, this.actionForm.sink)
          }
        })
      }
    },

    // change connectionSelector
    changeConnectionSelector(connection) {
      if (this.actionForm.sink) {
        const params = {
          nodeID: this.nodeID,
          name: this.actionForm.sink,
        }
        getMetadataYamlByCategory('connections', params).then((res) => {
          const { data: allConnectionData } = res
          // Override some of the current `proprties` values
          const currentConnectionData = allConnectionData[connection]
          this.actionPropertiesForm = {
            ...this.actionPropertiesForm,
            ...currentConnectionData,
          }
        })
      }
    },

    // show conf Key properties modal: add | edit
    showConfKeyModel(oper) {
      this.isEditResourceID = oper === 'edit'
      this.confKeyPropertiesVisible = true
    },
    // After `editing` or `adding` confKey(resourceID), load the latest confKey(resourceID) list and its data again.
    async submitedResourceID(data) {
      const { name } = data
      this.actionForm.resourceId = name
      this.$refs.confKeysSelectorRef.init()
      this.changeResourceID(name)
    },

    // Test connection
    async testConnection(type, body) {
      this.isLoadingTestConnection = true
      const data = {
        nodeID: this.nodeID,
        type,
      }
      testConnectionByCategory(this.category, data, body)
        .then(() => {
          this.$Message.success(this.$t('common.TestConnectionSuccess'))
        })
        .finally(() => {
          this.isLoadingTestConnection = false
        })
    },

    async getSubmitData() {
      try {
        const data = {}
        const { sink } = this.actionForm
        const validActionFormRes = await this.$refs.actionForm.validate()
        this.sameLevelFormValid = validActionFormRes

        // propertyForm validate
        const { validatePropertiesForm } = this.$refs.propertyFormRef
        await validatePropertiesForm()

        if (!validActionFormRes) {
          const errorMsg = this.$t('common.fillContent')
          this.$Message.error(errorMsg)
          return Promise.reject(new Error(errorMsg))
        }

        const propertyData = await this.$refs.propertyFormRef.getFormData()
        let advancedData = await this.$refs.advancedFormRef.getFormData()

        if (advancedData.connectionSelector) {
          advancedData = {
            ...advancedData,
            connectionSelector: `${sink}.${advancedData.connectionSelector}`,
          }
        }

        if (propertyData?.fields && propertyData.fields.length === 1 && !propertyData.fields[0]) {
          delete propertyData.fields
        }
        if (propertyData?.tagFields && propertyData.tagFields.length === 1 && !propertyData.tagFields[0]) {
          delete propertyData.tagFields
        }

        data[sink] = {
          resourceId: this.actionForm.resourceId,
          ...propertyData,
          ...advancedData,
          ...(advancedData.schemaName &&
            advancedData.schemaMessage && {
              schemaId: `${advancedData.schemaName}.${advancedData.schemaMessage}`,
            }),
          ...(advancedData.format === 'delimited' && { delimiter: advancedData.delimited }),
        }
        if (data[sink].delimited) {
          delete data[sink].delimited
        }
        data[sink] = removeObjectEmptyValue(data[sink])

        const action = {
          sink,
          data,
        }

        return Promise.resolve(action)
      } catch (error) {
        return Promise.reject(error)
      }
    },

    async save(oper) {
      try {
        const actionData = await this.getSubmitData()
        const { sink, data } = actionData

        if (oper === 'testConnection') {
          this.testConnection(this.actionForm.sink, { ...data[sink], type: sink })
        } else {
          if (this.isEditAction) {
            this.actions.splice(this.editIndex, 1, actionData)
          } else {
            this.actions.push(actionData)
          }
          this.viewModalVisible = false
          this.isEditAction = false
          this.editIndex = null
        }
      } catch (error) {
        console.log(error)
      }
    },

    // remove action
    removeAction(index) {
      this.actions.splice(index, 1)
    },
  },
}
</script>

<style lang="scss">
.actions-view {
  .ivu-table-wrapper {
    box-shadow: none;
    margin-top: 14px;
  }
}
.action-modal--view {
  .ivu-alert {
    display: flex;
    width: 20px;
    padding: 4px 40px 4px 11px;
  }
  .ivu-tooltip {
    &.field-help {
      cursor: pointer;
      font-size: 16px;
      position: relative;
      top: -1px;
      left: -2px;
    }
  }
  .ivu-table .ivu-icon {
    color: #fff;
  }
  .action-add-btn {
    margin-left: 10px;
  }
  .btn-test-connection {
    position: absolute;
    right: 20px;
  }
}
</style>
